// src/pages/Home.jsx
// Public landing page — trial-focused, no medical device claims

import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import './Home.css';

export default function Home({ theme, toggleTheme }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedPaper, setSelectedPaper] = useState(null);

  // Contact form state
  const [form, setForm] = useState({ name: '', email: '', org: '', message: '' });
  const [status, setStatus] = useState({ state: 'idle', msg: '' });

  // Redirect signed-in users to dashboard
  useEffect(() => {
    if (user) {
      navigate('/dashboard', { replace: true });
    }
  }, [user, navigate]);

  // Scroll to #contact when arriving from Privacy/Terms links
  useEffect(() => {
    if (location.hash) {
      const el = document.getElementById(location.hash.slice(1));
      if (el) setTimeout(() => el.scrollIntoView({ behavior: 'smooth' }), 50);
    }
  }, [location]);

  // Publications data — full abstracts, preprints labelled
  const publications = [
    {
      id: 1,
      year: '2026',
      title: 'Dual model deep learning for Alzheimer prognostication',
      journal: 'Computers in Biology and Medicine',
      authors: 'S. Fin, A. Moayedikia, U. K. Wiil',
      isPreprint: false,
      abstract: `Disease-modifying therapies for Alzheimer's disease demand precise timing decisions, yet current predictive models require longitudinal clinical observations and provide no uncertainty quantification—rendering them impractical at the critical first-visit encounter when treatment decisions must be made. We developed PROGRESS (PRognostic Generalization from REsting Static Signatures), a dual-model deep learning framework that transforms a single baseline cerebrospinal fluid (CSF) biomarker assessment into actionable prognostic estimates without requiring prior clinical history. The framework addresses two complementary clinical questions: a probabilistic trajectory network predicts individualized cognitive decline parameters with calibrated uncertainty bounds that achieve near-nominal coverage, enabling honest prognostic communication rather than false precision; and a deep survival model estimates time-to-conversion from mild cognitive impairment to dementia. Using data from over 3000 participants across 43 Alzheimer's Disease Research Centers in the National Alzheimer's Coordinating Center database, PROGRESS substantially outperforms existing approaches including Cox proportional hazards, Random Survival Forests, and gradient boosting methods for survival prediction. Risk stratification identifies patient groups with seven-fold differences in conversion rates, enabling clinically meaningful treatment prioritization. Leave-one-center-out validation demonstrates robust generalizability, with survival discrimination remaining strong across all held-out clinical sites despite heterogeneous measurement conditions spanning four decades of assay technologies. By combining superior survival prediction with trustworthy trajectory uncertainty quantification, PROGRESS bridges the gap between biomarker measurement and personalized clinical decision-making—providing the prognostic timeline that current staging approaches cannot offer.`,
      link: 'https://arxiv.org/abs/2512.19099',
    },
    {
      id: 2,
      year: '2026',
      title: 'Feature selection for high-dimensional imbalanced datasets using Harmony Search and Kullback-Leibler divergence',
      journal: 'Data Mining and Knowledge Discovery',
      authors: 'A. Moayedikia, R. Jensen, S. Fin',
      isPreprint: false,
      abstract: `High-dimensional imbalanced datasets pose significant challenges in pattern recognition, often leading to overfitting and classifier bias toward majority classes. While numerous feature selection algorithms exist, most struggle to effectively address both high dimensionality and class imbalance simultaneously. This paper introduces Harmony Search Kullback–Leibler (HKL), a novel feature selection algorithm that integrates Kullback–Leibler divergence with the Harmony Search metaheuristic to specifically address these dual challenges. HKL establishes an information-theoretic foundation by employing KL divergence as a statistical framework to evaluate feature subsets based on their ability to separate minority and majority classes. Unlike existing Harmony Search variants that operate as class-blind optimizers treating feature selection as a generic optimization problem, HKL fundamentally shifts the paradigm by incorporating direct class distribution awareness into the optimization process. The algorithm implements a dual optimization approach that simultaneously balances classification performance metrics with class distribution divergence measures. This design specifically enhances minority class discrimination by prioritizing features that maximize the divergence between class distributions, ensuring that selected features provide discriminative power for underrepresented classes rather than simply favoring the majority class. Experimental validation across multiple high-dimensional biomedical datasets demonstrates that HKL consistently outperforms existing state-of-the-art methods in terms of AUC and G-mean metrics, with particular improvements for minority class classification.`,
      link: 'https://link.springer.com/article/10.1007/s10618-025-01172-w',
    },
    {
      id: 3,
      year: '2025',
      title: 'Multi-objective optimization for Alzheimer\'s disease trial patient selection',
      journal: 'Journal of Biomedical Informatics',
      authors: 'A. Moayedikia, S. Fin, U. K. Wiil',
      isPreprint: false,
      abstract: `Clinical trial recruitment faces critical challenges with screen failure rates exceeding 80% in Alzheimer's disease (AD) trials. Traditional patient selection relies on expert consensus without systematic evaluation of trade-offs between statistical power, recruitment feasibility, safety, and cost. We developed a multi-objective optimization framework to systematically identify optimal eligibility criteria configurations that balance competing objectives in AD clinical trial design. We implemented the Non-dominated Sorting Genetic Algorithm III (NSGA-III) to optimize patient selection criteria across three objectives: patient identification accuracy (F1 score), recruitment balance, and economic efficiency. The framework utilized National Alzheimer's Coordinating Center data comprising 2,743 participants with comprehensive clinical assessments and cerebrospinal fluid biomarker measurements. We optimized 14 eligibility parameters including age boundaries, cognitive thresholds, biomarker criteria, and comorbidity management policies. Optimization identified 11 Pareto-optimal solutions. Monte Carlo simulation revealed mean cost savings per patient with 80.7% probability of positive savings, though importantly this also implies a 19.3% probability of cost increase under certain configurations. Cross-validation demonstrated high precision with strategic selectivity. SHAP analysis identified biomarker requirements as the dominant cost driver. Multi-objective optimization provides incremental value through systematic validation and probabilistic efficiency enhancement. The convergence toward established practice demonstrates that computational approaches serve as sophisticated validation tools that identify concrete efficiency improvements within existing frameworks, rather than replacing clinical judgment.`,
      link: 'https://www.sciencedirect.com/science/article/pii/S1532046425001844',
    },
    {
      id: 4,
      year: '2025',
      title: 'Alzheimer\'s disease brain network mining',
      journal: 'arXiv preprint',
      authors: 'A. Moayedikia, S. Fin',
      isPreprint: true,
      abstract: `Machine learning approaches for Alzheimer's disease (AD) diagnosis face a fundamental challenge. Clinical assessments are expensive and invasive, leaving ground truth labels available for only a fraction of neuroimaging datasets. We introduce Multi view Adaptive Transport Clustering for Heterogeneous Alzheimer's Disease (MATCH-AD), a semi-supervised framework that integrates deep representation learning, graph-based label propagation, and optimal transport theory to address this limitation. The framework leverages manifold structure in neuroimaging data to propagate diagnostic information from limited labeled samples to larger unlabeled populations, while using Wasserstein distances to quantify disease progression between cognitive states. Evaluated on nearly five thousand subjects from the National Alzheimer's Coordinating Center, encompassing structural MRI measurements from hundreds of brain regions, cerebrospinal fluid biomarkers, and clinical variables, MATCH-AD achieves near-perfect diagnostic accuracy despite ground truth labels for less than one-third of subjects. The framework substantially outperforms all baseline methods, achieving kappa indicating almost perfect agreement compared to weak agreement for the best baseline, a qualitative transformation in diagnostic reliability. Performance remains clinically useful even under severe label scarcity, and we provide theoretical convergence guarantees with proven bounds on label propagation error and transport stability.`,
      link: 'https://arxiv.org/abs/2512.17276',
    },
  ];

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      setStatus({ state: 'error', msg: 'Please fill in name, email, and message.' });
      return;
    }
    setStatus({ state: 'sending', msg: 'Sending…' });
    try {
      const emailjs = await import('@emailjs/browser');
      await emailjs.send(
        'service_jp2ebfm',
        'template_xz7a7ya',
        {
          from_name: form.name,
          from_email: form.email,
          from_org: form.org || 'Not specified',
          message: form.message,
        },
        { publicKey: 'pbClQ6nhAf5Ij9Y4-' }
      );
      setStatus({ state: 'sent', msg: 'Thank you — we\'ll be in touch.' });
      setForm({ name: '', email: '', org: '', message: '' });
    } catch (err) {
      console.error(err);
      setStatus({ state: 'error', msg: 'Sending failed. Please try again.' });
    }
  };

  const openModal = (paper) => {
    setSelectedPaper(paper);
    document.body.style.overflow = 'hidden';
  };

  const closeModal = () => {
    setSelectedPaper(null);
    document.body.style.overflow = 'auto';
  };

  return (
    <div className="landing">
      {/* ===== HEADER BAR ===== */}
      <header className="landing__header">
        <div className="container landing__header-inner">
          <Link to="/" className="landing__brand">
            <img src="/logo-full.png" alt="BrainAxis" className="landing__logo" />
          </Link>
          <button
            className="landing__theme"
            onClick={toggleTheme}
            aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
          >
            {theme === 'dark' ? '☀' : '☾'}
          </button>
        </div>
      </header>

      {/* ===== HERO ===== */}
      <section className="hero">
        <div className="container hero__inner">
          <h1 className="hero__title">Alzheimer's imaging for clinical trials</h1>
          <p className="hero__lead">
            Machine learning tools for imaging analysis, trial enrichment and patient selection.
          </p>
        </div>
      </section>

      {/* ===== WHAT WE DO ===== */}
      <section className="products">
        <div className="container">
          <h2 className="section-title">What we do</h2>
          <div className="products__grid products__grid--three">
            <div className="product-card">
              <span className="product-card__label">Recruitment</span>
              <h3 className="product-card__title">Patient selection</h3>
              <p className="product-card__desc">
                Eligibility prediction and enrichment tools that reduce screen-failure rates 
                and accelerate recruitment for Alzheimer's disease clinical trials.
              </p>
            </div>
            <div className="product-card">
              <span className="product-card__label">Analysis</span>
              <h3 className="product-card__title">Imaging endpoints</h3>
              <p className="product-card__desc">
                Quantitative, reproducible measurement from structural MRI and PET across 
                timepoints, reducing reader variability in trial imaging analysis.
              </p>
            </div>
            <div className="product-card">
              <span className="product-card__label">Prediction</span>
              <h3 className="product-card__title">Multimodal analysis</h3>
              <p className="product-card__desc">
                Fusion of imaging with clinical and biomarker data to predict progression 
                within trial windows, using modalities trials already acquire.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ===== TEAM ===== */}
      <section className="team">
        <div className="container">
          <h2 className="section-title">Our team</h2>
          <p className="team__intro">
            BrainAxis combines machine learning research with translational neuroscience 
            and drug development experience.
          </p>
          <div className="team__capabilities">
            <div className="capability">
              <h4 className="capability__title">Medical AI and deep learning</h4>
              <p className="capability__desc">
                Multimodal fusion, longitudinal modelling and semi-supervised learning 
                applied to neuroimaging.
              </p>
            </div>
            <div className="capability">
              <h4 className="capability__title">Translational neuroscience</h4>
              <p className="capability__desc">
                Preclinical and clinical research, biomarker development for 
                neurodegenerative disease.
              </p>
            </div>
            <div className="capability">
              <h4 className="capability__title">Peer-reviewed research</h4>
              <p className="capability__desc">
                Published in Journal of Biomedical Informatics, Computers in Biology 
                and Medicine, and Data Mining and Knowledge Discovery.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ===== PLATFORM ===== */}
      <section className="platform">
        <div className="container">
          <h2 className="section-title">Platform</h2>
          <p className="platform__text">
            BrainAxis operates a secure analysis platform for neuroimaging research. 
            Approved users upload de-identified imaging data, review analysis outputs, 
            and track cases across timepoints. Access is by invitation, and the platform 
            is for research use only.
          </p>
        </div>
      </section>

      {/* ===== RESEARCH ===== */}
      <section className="research">
        <div className="container">
          <h2 className="section-title">Research</h2>
          <p className="research__intro">
            Published and preprint research from the BrainAxis team.
          </p>
          <div className="publications">
            {publications.map((pub) => (
              <div key={pub.id} className="pub-item" onClick={() => openModal(pub)}>
                <span className="pub-item__year">{pub.year}</span>
                <div className="pub-item__content">
                  <h4 className="pub-item__title">{pub.title}</h4>
                  <p className="pub-item__meta">
                    {pub.journal}
                    {pub.isPreprint && <span className="pub-item__preprint"> (preprint)</span>}
                  </p>
                </div>
                <span className="pub-item__arrow">→</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== CONTACT ===== */}
      <section id="contact" className="contact">
        <div className="container">
          <h2 className="section-title">Get in touch</h2>
          <p className="contact__intro">
            For trial sponsors, imaging core labs and research partnerships.
          </p>
          <form className="contact__form" onSubmit={handleSubmit}>
            <div className="contact__row">
              <input type="text" name="name" placeholder="Name"
                value={form.name} onChange={handleChange} required />
              <input type="email" name="email" placeholder="Email"
                value={form.email} onChange={handleChange} required />
            </div>
            <input type="text" name="org" placeholder="Organisation (optional)"
              value={form.org} onChange={handleChange} />
            <textarea name="message" placeholder="Message" rows="4"
              value={form.message} onChange={handleChange} required />
            <button type="submit" className="btn btn-primary"
              disabled={status.state === 'sending'}>
              {status.state === 'sending' ? 'Sending…' : 'Send message'}
            </button>
            {status.msg && (
              <div className={`contact__status contact__status--${status.state}`}>
                {status.msg}
              </div>
            )}
          </form>
        </div>
      </section>

      {/* ===== FOOTER ===== */}
      <footer className="footer">
        <div className="container footer__inner">
          <div className="footer__company">
            <p className="footer__name">BrainAxis Pty Ltd</p>
            <p className="footer__detail">ACN 696 339 947</p>
            <p className="footer__detail">Melbourne, Victoria, Australia</p>
          </div>
          <div className="footer__links">
            <Link to="/privacy">Privacy Policy</Link>
            <Link to="/terms">Terms of Use</Link>
          </div>
          <p className="footer__copyright">© 2026 BrainAxis Pty Ltd</p>
        </div>
      </footer>

      {/* ===== MODAL ===== */}
      {selectedPaper && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={closeModal}>×</button>
            <div className="modal-header">
              <span className="modal-year">{selectedPaper.year}</span>
              <span className="modal-journal">
                {selectedPaper.journal}
                {selectedPaper.isPreprint && ' (preprint)'}
              </span>
            </div>
            <h2 className="modal-title">{selectedPaper.title}</h2>
            <p className="modal-authors">{selectedPaper.authors}</p>
            <div className="modal-abstract">
              <h4>Abstract</h4>
              <p>{selectedPaper.abstract}</p>
            </div>
            <a
              href={selectedPaper.link}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-primary"
            >
              View publication ↗
            </a>
          </div>
        </div>
      )}
    </div>
  );
}
